"use strict";
// Copyright (c) 2026 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlindSignatureRequest = exports.BlindSigner = void 0;
exports.buildBlindSigRequests = buildBlindSigRequests;
exports.finalizeBlindSigs = finalizeBlindSigs;
/**
 * Schnorr blind signatures (CashFusion / Electrum ABC).
 * Port of electrumabc/schnorr.py BlindSigner + BlindSignatureRequest.
 *
 * Secret scalars are Uint8Array values combined with wasm ECC
 * (`seckeyAdd` / `seckeyMul` / `seckeyNegate`), which reduce mod n.
 */
const ecc_js_1 = require("./ecc.js");
const eccScalar_js_1 = require("./eccScalar.js");
const hash_js_1 = require("./hash.js");
const array_js_1 = require("./io/array.js");
const ecc = new ecc_js_1.Ecc();
const FIELD_SIZE = 0xfffffffffffffffffffffffffffffffffffffffffffffffffffffffefffffc2fn;
/** Jacobi symbol — port of electrumabc/schnorr.py jacobi(). Public inputs only. */
function jacobi(a, n) {
    let x = ((a % n) + n) % n;
    if (n < 3n || (n & 1n) === 0n) {
        throw new Error('jacobi: bad n');
    }
    let s = 1;
    while (x > 1n) {
        let a1 = x;
        let e = 0;
        while ((a1 & 1n) === 0n) {
            a1 >>= 1n;
            e++;
        }
        if (e % 2 === 1 && n % 8n !== 1n && n % 8n !== 7n) {
            s = -s;
        }
        if (a1 === 1n) {
            return s;
        }
        if (n % 4n === 3n && a1 % 4n === 3n) {
            s = -s;
        }
        x = n % a1;
        n = a1;
    }
    if (x === 0n) {
        return 0;
    }
    if (x === 1n) {
        return s;
    }
    return 0;
}
function assertCompressedPoint(name, pk) {
    if (pk.length !== 33 || (pk[0] !== 0x02 && pk[0] !== 0x03)) {
        throw new Error(`${name} must be a 33-byte compressed secp256k1 point`);
    }
    try {
        ecc.uncompressPk(pk);
    }
    catch {
        throw new Error(`${name} is not a valid secp256k1 point`);
    }
}
/** Server-side: one nonce R per component slot; signs once. */
class BlindSigner {
    constructor() {
        this.k = (0, eccScalar_js_1.randomScalarBytes)();
        this.rPoint = ecc.derivePubkey(this.k);
    }
    /** Compressed nonce point R = k*G (defensive copy). */
    getR() {
        return new Uint8Array(this.rPoint);
    }
    sign(roundSeckey, ebytes) {
        if (ebytes.length !== 32 || roundSeckey.length !== 32) {
            throw new Error('BlindSigner: expected 32-byte key and challenge');
        }
        const k = this.k;
        if (k === undefined) {
            throw new Error('BlindSigner: attempted to sign twice');
        }
        this.k = undefined;
        try {
            // s = (k + e*x) mod n
            const ex = ecc.seckeyMul(roundSeckey, ebytes);
            try {
                return ecc.seckeyAdd(k, ex);
            }
            finally {
                (0, array_js_1.zeroBytes)(ex);
            }
        }
        finally {
            (0, array_js_1.zeroBytes)(k);
        }
    }
}
exports.BlindSigner = BlindSigner;
/** Client-side: blinds component hash; unblinds server scalar to Schnorr sig. */
class BlindSignatureRequest {
    constructor(pubkey, R, messageHash) {
        if (messageHash.length !== 32) {
            throw new Error('message_hash must be 32 bytes');
        }
        // Fail fast on bad inputs; the retry loop only covers rare EC degeneracy
        // / jacobi-0 (inputs already validated).
        assertCompressedPoint('pubkey', pubkey);
        assertCompressedPoint('R', R);
        this.messageHash = new Uint8Array(messageHash);
        this.pubkeyCompressed = new Uint8Array(pubkey);
        // R' = R + a*G + b*P; c = jacobi(R'.y) ∈ {±1}.
        for (let attempt = 0; attempt < 32; attempt++) {
            const a = (0, eccScalar_js_1.randomScalarBytes)();
            const b = (0, eccScalar_js_1.randomScalarBytes)();
            try {
                const bP = ecc.pubkeyMul(pubkey, b);
                const RplusBP = ecc.pubkeyCombine(R, bP);
                const combined = ecc.pubkeyAdd(RplusBP, a);
                const uncompressed = ecc.uncompressPk(combined);
                const Rxnew = uncompressed.subarray(1, 33);
                const y = (0, array_js_1.bytesToScalar)(uncompressed.subarray(33));
                const c = jacobi(y, FIELD_SIZE);
                if (c !== 1 && c !== -1) {
                    (0, array_js_1.zeroBytes)(a);
                    (0, array_js_1.zeroBytes)(b);
                    continue;
                }
                // ehash is public; reduce with bigint then combine with secret b via wasm.
                const ehash = (0, hash_js_1.sha256)((0, array_js_1.concatBytes)(Rxnew, this.pubkeyCompressed, this.messageHash));
                const ehashMod = (0, eccScalar_js_1.modCurveOrder)((0, array_js_1.bytesToScalar)(ehash));
                const ehashTweak = (0, eccScalar_js_1.scalarToBytes)(c === 1 ? ehashMod : (0, eccScalar_js_1.modCurveOrder)(-ehashMod));
                // e = (b + c*ehash) mod n
                const e = ecc.seckeyAdd(b, ehashTweak);
                this.a = new Uint8Array(a);
                this.c = c;
                this.Rxnew = new Uint8Array(Rxnew);
                this.e = e;
                (0, array_js_1.zeroBytes)(a);
                (0, array_js_1.zeroBytes)(b);
                return;
            }
            catch {
                // Point at infinity or other rare EC failure (~2^-256) — retry.
                (0, array_js_1.zeroBytes)(a);
                (0, array_js_1.zeroBytes)(b);
            }
        }
        throw new Error("BlindSignatureRequest: failed to find valid R' after retries");
    }
    getRequest() {
        return new Uint8Array(this.e);
    }
    finalize(sbytes, check = true) {
        if (sbytes.length !== 32) {
            throw new Error('s must be 32 bytes');
        }
        const a = this.a;
        if (a === undefined) {
            throw new Error('BlindSignatureRequest: already finalized');
        }
        this.a = undefined;
        try {
            // s' = c*(s + a) mod n for c ∈ {±1}:
            // always compute (s + a) mod n, then negate when c == -1.
            const sPlusA = ecc.seckeyAdd(sbytes, a);
            let sFinal;
            if (this.c === -1) {
                sFinal = ecc.seckeyNegate(sPlusA);
                // Intermediate still mixes secret a; s' in the sig is public.
                (0, array_js_1.zeroBytes)(sPlusA);
            }
            else {
                sFinal = sPlusA;
            }
            const sig = (0, array_js_1.concatBytes)(this.Rxnew, sFinal);
            if (check) {
                ecc.schnorrVerify(sig, this.messageHash, this.pubkeyCompressed);
            }
            return sig;
        }
        finally {
            (0, array_js_1.zeroBytes)(a);
        }
    }
}
exports.BlindSignatureRequest = BlindSignatureRequest;
function buildBlindSigRequests(roundPubkey, blindNoncePoints, components) {
    if (components.length > blindNoncePoints.length) {
        throw new Error('not enough blind nonce points');
    }
    const requests = components.map((comp, i) => new BlindSignatureRequest(roundPubkey, blindNoncePoints[i], (0, hash_js_1.sha256)(comp)));
    return {
        requests,
        eValues: requests.map(r => r.getRequest()),
    };
}
function finalizeBlindSigs(requests, scalars) {
    if (requests.length !== scalars.length) {
        throw new Error('blind scalar count mismatch');
    }
    return requests.map((r, i) => r.finalize(scalars[i]));
}
//# sourceMappingURL=blindSchnorr.js.map